import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, PencilSimple, Trash, Printer, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import { openPrintStatement } from "@/lib/printStatement";

export default function PurchaseInvoices() {
  const { activeCompanyId, activeCompany, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/purchase-invoices"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const del = async (id) => {
    if (!confirm("Delete this purchase?")) return;
    try { await api.delete(`/purchase-invoices/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (start && r.date < start) return false;
      if (end && r.date > end) return false;
      if (status !== "all" && r.status !== status) return false;
      if (qn) {
        const sup = (r.supplier_name || "").toLowerCase();
        const num = (r.number || "").toLowerCase();
        const bill = (r.supplier_bill_number || "").toLowerCase();
        if (!sup.includes(qn) && !num.includes(qn) && !bill.includes(qn)) return false;
      }
      return true;
    });
  }, [rows, q, start, end, status]);

  const totals = useMemo(() => {
    const grand = filtered.reduce((s, r) => s + Number(r.grand_total || 0), 0);
    const bal = filtered.reduce((s, r) => s + Number(r.balance_due || 0), 0);
    return { count: filtered.length, grand, bal };
  }, [filtered]);

  const resetFilters = () => { setQ(""); setStart(""); setEnd(""); setStatus("all"); };
  const hasActiveFilters = q || start || end || status !== "all";

  const exportColumns = [
    { key: "number", label: "Number" },
    { key: "supplier_bill_number", label: "Bill #" },
    { key: "date", label: "Date" },
    { key: "supplier_name", label: "Supplier" },
    { key: "status", label: "Status" },
    { key: "grand_total", label: "Grand Total" },
    { key: "paid_amount", label: "Paid" },
    { key: "balance_due", label: "Balance Due" },
    { key: "payment_mode", label: "Payment Mode" },
  ];

  const printStatement = () => {
    if (!filtered.length) { toast.error("No purchase invoices in the current filter."); return; }
    const groups = {};
    filtered.forEach((r) => { (groups[r.status] = groups[r.status] || []).push(r); });
    const label = { paid: "Paid Purchases", partial: "Partially Paid", unpaid: "Unpaid / Open", cancelled: "Cancelled" };
    const cols = [
      { key: "number", label: "PO #", mono: true },
      { key: "bill", label: "Bill #", mono: true },
      { key: "date", label: "Date", mono: true },
      { key: "supplier", label: "Supplier" },
      { key: "amount", label: "Amount", align: "right", mono: true },
      { key: "balance", label: "Balance Due", align: "right", mono: true },
    ];
    const sections = Object.keys(groups).map((k) => {
      const list = groups[k];
      const sub = list.reduce((s, r) => s + Number(r.grand_total || 0), 0);
      const bal = list.reduce((s, r) => s + Number(r.balance_due || 0), 0);
      return {
        heading: label[k] || k.toUpperCase(),
        subheading: `${list.length} purchase(s) · Total <b>${inr(sub)}</b>${bal ? ` · Balance Due <b>${inr(bal)}</b>` : ""}`,
        columns: cols,
        rows: list.map((r) => ({
          number: r.number,
          bill: r.supplier_bill_number || "—",
          date: r.date,
          supplier: r.supplier_name || "—",
          amount: inr(r.grand_total),
          balance: inr(r.balance_due),
        })),
      };
    });
    const range = start || end ? `${start || "…"} → ${end || "…"}` : "All dates";
    const res = openPrintStatement({
      title: `Purchase Invoices Statement (${range})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Purchase Count", value: String(totals.count) },
        { label: "Grand Total", value: inr(totals.grand) },
        { label: "Balance Due", value: inr(totals.bal), tone: totals.bal > 0 ? "neg" : "" },
      ],
      note: hasActiveFilters ? `Filters applied — ${totals.count} purchase(s) shown.` : "Full purchase list.",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${totals.count} purchase(s)…`);
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Procurement</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Purchase Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totals.count} of {rows.length} purchase(s) · Total <b>{inr(totals.grand)}</b>{totals.bal ? <> · Balance Due <b className="text-destructive">{inr(totals.bal)}</b></> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="purchases-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Purchase Invoices"
            filename={`purchase-invoices-${new Date().toISOString().slice(0,10)}`}
            columns={exportColumns}
            rows={filtered}
            testidPrefix="purchases"
          />
          <Link to="/purchases/new">
            <Button className="h-9" data-testid="new-purchase-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Purchase
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-3 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5">
          <Label className="eyebrow">From Date</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)}
            className="h-9 font-mono w-40" data-testid="purchases-filter-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To Date</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)}
            className="h-9 font-mono w-40" data-testid="purchases-filter-end" />
        </div>
        <div className="space-y-1.5 flex-1 min-w-[220px]">
          <Label className="eyebrow">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="PO #, Bill # or supplier name"
              className="pl-9 h-9" data-testid="purchases-filter-q" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="purchases-filter-status">
            <option value="all">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" className="h-9" onClick={resetFilters} data-testid="purchases-filter-reset">
            <X size={14} className="mr-1.5" /> Clear
          </Button>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Bill #</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Status</th>
              <th className="num">Grand Total</th>
              <th className="num">Balance Due</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">
                {rows.length === 0 ? "No purchase invoices yet." : "No purchases match the current filter."}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`pur-row-${r.id}`}>
                <td className="font-mono text-[12.5px]">
                  <Link to={`/purchases/${r.id}`} className="text-primary hover:underline">{r.number}</Link>
                </td>
                <td className="font-mono text-[12px]">{r.supplier_bill_number || "—"}</td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.supplier_name}</td>
                <td><span className={`pill ${r.status === "paid" ? "pill-success" : r.status === "partial" ? "pill-warning" : "pill-info"}`}>{r.status}</span></td>
                <td className="num font-semibold">{inr(r.grand_total)}</td>
                <td className="num">{inr(r.balance_due)}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <Link to={`/purchases/${r.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8"><PencilSimple size={14} /></Button>
                  </Link>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`pur-del-${r.id}`}>
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
